import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import ContinuousCodeEditor from "./ContinuousCodeEditor";
import TabbedResultPanel from "./TabbedResultPanel";
import { Link } from "react-router-dom";
import { ChevronDown, Play, AlertCircle, Check, Menu, X, Eye } from "lucide-react";
import SolutionDiffModal from "./SolutionDiffModal";
import { executeLesson, setSolutions } from "../../features/courses/courseSlice";
import Header from "../Practice/Header";
import usePracticeData from "../../../hooks/usePracticeDataFromRole";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import Split from "react-split";

const ContinuousPractice = () => {
  const { selectedRole, lessonList, handleRoleChange, courseDetails } =
    usePracticeData();

  const [horizontalSplitSizes, setHorizontalSplitSizes] = useState([60, 40]);

  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const editorRef = useRef(null);

  const [selectedLesson, setSelectedLesson] = useState(null);
  const [executionStatusMap, setExecutionStatusMap] = useState({});
  const [debugLogsMap, setDebugLogsMap] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [contentHeight, setContentHeight] = useState("short");
  const [showSolutionModal, setShowSolutionModal] = useState(false);
  const [attemptCountMap, setAttemptCountMap] = useState({});

  const practiceData = {
    data: [
      {
        slug: "variables",
        type: "Variables",
        description: courseDetails.description,
      },
      {
        slug: "scope",
        type: "Scope",
        description: courseDetails.description,
      },
      {
        slug: "collections",
        type: "Collections",
        description: courseDetails.description,
      },
      {
        slug: "conditionals",
        type: "Conditionals",
        description: courseDetails.description,
      },
      {
        slug: "loops",
        type: "Loops",
        description: courseDetails.description,
      },
      {
        slug: "classes",
        type: "Classes",
        description: courseDetails.description,
      },
      {
        slug: "methods",
        type: "Methods",
        description: courseDetails.description,
      },
      {
        slug: "lists",
        type: "Lists",
        description: courseDetails.description,
      },
            {
        slug: "maps",
        type: "Maps",
        description: courseDetails.description,
      },
                  {
        slug: "sets",
        type: "Sets",
        description: courseDetails.description,
      },
      {
        slug: "sobjects",
        type: "SObjects",
        description: courseDetails.description,
      },
      {
        slug: "oo",
        type: "OO",
        description: courseDetails.description,
      },
      {
        slug: "dml",
        type: "DML",
        description: courseDetails.description,
      },
                  {
        slug: "soql",
        type: "SOQL",
        description: courseDetails.description,
      },
      {
        slug: "builder",
        type: "Builder",
        description: courseDetails.description,
      },
      { slug: "flow-into-apex", type: "Flow Into Apex" },
    ],
  };

  useEffect(() => {
    if (lessonList?.length > 0) {
      setSelectedLesson(lessonList[0]);
      const statusMap = lessonList.reduce((map, lesson) => {
        if (lesson.progress?.[0]) {
          map[lesson._id] = {
            data: { success: lesson.progress[0].status === "Completed" },
          };
        }
        return map;
      }, {});
      setExecutionStatusMap(statusMap);

      // Initialize attempt count map from lesson progress
      const attemptMap = lessonList.reduce((map, lesson) => {
        if (lesson.progress?.[0]) {
          map[lesson._id] = lesson.progress[0].attemptCounter || 0;
        }
        return map;
      }, {});
      setAttemptCountMap(attemptMap);
    }
  }, [lessonList]);

  useEffect(() => {
    if (selectedLesson?.lesson) {
      const contentLength = selectedLesson.lesson.length;
      setContentHeight(contentLength > 300 ? "long" : "short");
    }
  }, [selectedLesson]);

  // Check if we're on mobile and default the sidebar to closed
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleRunCode = async (lesson) => {
    try {
      const lessonSolution = editorRef.current?.getCurrentContent();
      if (!lessonSolution) return;

      const result = await dispatch(
        executeLesson({
          lessonId: lesson._id,
          apexBody: lessonSolution.trim(),
          lessonType: "continuous",
        })
      ).unwrap();

      // Save the solution to Redux so it persists when switching challenges
      dispatch(setSolutions({ lessonId: lesson._id, value: lessonSolution }));

      setExecutionStatusMap((prev) => ({
        ...prev,
        [lesson._id]: result,
      }));

      setDebugLogsMap((prev) => ({
        ...prev,
        [lesson._id]: result.debugLogs,
      }));

      // Update attempt count from result
      if (result.submissionResults?.attemptCounter !== undefined) {
        setAttemptCountMap((prev) => ({
          ...prev,
          [lesson._id]: result.submissionResults.attemptCounter,
        }));
      }
    } catch (error) {
      console.error("Error running code:", error);
      setExecutionStatusMap((prev) => ({
        ...prev,
        [lesson._id]: {
          data: {
            success: false,
            compileProblem: "An error occurred while running the code",
          },
        },
      }));
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="bg-zinc-900 min-h-screen font-mono text-zinc-100">
      {/* <Header
        initialRole={""}
        selectedRole={selectedRole}
        practiceData={practiceData}
        handleRoleChange={handleRoleChange}
      /> */}

      {/* Main wrapper with shared background */}
      <div className="h-screen overflow-hidden">
        <div className="bg-zinc-800/20 border border-zinc-700/50 shadow-lg h-full">
          <div className="flex h-full">
            {/* Sidebar Toggle for Mobile */}
            <button
              className="md:hidden fixed bottom-4 right-4 z-50 bg-lime-600 hover:bg-lime-700 text-white p-3 rounded-full shadow-lg transition-colors"
              onClick={toggleSidebar}
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>

            {/* Left Sidebar */}
            <div
              className={`${
                sidebarOpen
                  ? "translate-x-0 md:w-80"
                  : "-translate-x-full md:translate-x-0 md:w-12"
              } transform transition-all duration-300 ease-in-out fixed md:relative z-40 md:z-auto h-full bg-zinc-800/40 md:bg-transparent md:border-r border-zinc-700/50 flex flex-col`}
            >
              {/* Sidebar Header with Course Selection */}
              <div
                className={`bg-gradient-to-r from-zinc-800/80 to-zinc-800/60 border-b border-zinc-700/50 flex-shrink-0 transition-all duration-300 ${
                  sidebarOpen ? "px-4 py-4" : "px-2 py-3"
                }`}
              >
                {sidebarOpen ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-medium text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-cyan-400">
                        Challenges
                      </h2>
                      <button
                        className="text-zinc-400 hover:text-lime-400 transition-colors"
                        onClick={toggleSidebar}
                        aria-label="Collapse sidebar"
                      >
                        <ChevronDown
                          size={20}
                          className="transform rotate-[-90deg]"
                        />
                      </button>
                    </div>

                    {/* Course Selection Dropdown */}
                    <div className="relative">
                      <select
                        value={
                          selectedRole === "{Your Role}"
                            ? ""
                            : selectedRole.toLowerCase().replace(" ", "-")
                        }
                        onChange={(e) => handleRoleChange(e.target.value)}
                        className="w-full bg-zinc-700/50 border border-zinc-600/50 rounded-md px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
                      >
                        <option value="" disabled>
                          {selectedRole === "{Your Role}"
                            ? "Select Role"
                            : selectedRole}
                        </option>
                        {practiceData.data.map((role) => (
                          <option key={role.slug} value={role.slug}>
                            {role.type}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  /* Collapsed State Header */
                  <div className="flex flex-col items-center space-y-2">
                    {/* Role indicator when collapsed */}
                    {selectedRole !== "{Your Role}" && (
                      <div className="w-8 h-8 bg-lime-500/20 rounded-full flex items-center justify-center text-xs text-lime-300 font-bold">
                        {selectedRole.charAt(0)}
                      </div>
                    )}
                    <button
                      className="text-zinc-400 hover:text-lime-400 transition-colors"
                      onClick={toggleSidebar}
                      aria-label="Expand sidebar"
                    >
                      <ChevronDown size={20} className="transform rotate-90" />
                    </button>
                  </div>
                )}
              </div>

              {/* Scrollable Content Area */}
              {sidebarOpen ? (
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                  {lessonList?.map((lesson) => (
                    <button
                      key={lesson._id}
                      onClick={() => {
                        // Save current editor content before switching
                        if (selectedLesson?._id) {
                          const currentContent = editorRef.current?.getCurrentContent();
                          if (currentContent) {
                            dispatch(setSolutions({ lessonId: selectedLesson._id, value: currentContent }));
                          }
                        }
                        setSelectedLesson(lesson);
                        if (window.innerWidth < 768) {
                          setSidebarOpen(false);
                        }
                      }}
                      className={`w-full flex items-center px-6 py-3 text-left hover:bg-zinc-700/30 transition-colors border-b border-zinc-700/30 ${
                        selectedLesson?._id === lesson._id
                          ? "bg-zinc-700/40"
                          : ""
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div
                          className={`flex items-center justify-center w-7 h-7 rounded-full ${
                            executionStatusMap[lesson._id]?.data?.success
                              ? "bg-lime-500/20 text-lime-300"
                              : "bg-zinc-700 text-zinc-300"
                          }`}
                        >
                          {executionStatusMap[lesson._id]?.data?.success ? (
                            <Check size={12} />
                          ) : (
                            <span>{lesson.order + 1}</span>
                          )}
                        </div>
                        <h3
                          className={`text-base font-medium ${
                            executionStatusMap[lesson._id]?.data?.success
                              ? "text-lime-300"
                              : "text-zinc-200"
                          }`}
                        >
                          {lesson.name}
                        </h3>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex-1 pt-4 overflow-y-auto px-2 space-y-2">
                  {lessonList?.map((lesson) => (
                    <button
                      key={lesson._id}
                      onClick={() => {
                        // Save current editor content before switching
                        if (selectedLesson?._id) {
                          const currentContent = editorRef.current?.getCurrentContent();
                          if (currentContent) {
                            dispatch(setSolutions({ lessonId: selectedLesson._id, value: currentContent }));
                          }
                        }
                        setSelectedLesson(lesson);
                        setSidebarOpen(true); // Auto-expand when lesson selected
                      }}
                      className={`w-full flex items-center justify-center p-2 rounded hover:bg-zinc-700/30 transition-colors ${
                        selectedLesson?._id === lesson._id
                          ? "bg-zinc-700/40"
                          : ""
                      }`}
                      title={lesson.name}
                    >
                      <div
                        className={`flex items-center justify-center w-6 h-6 rounded-full text-xs ${
                          executionStatusMap[lesson._id]?.data?.success
                            ? "bg-lime-500/20 text-lime-300"
                            : "bg-zinc-700 text-zinc-300"
                        }`}
                      >
                        {executionStatusMap[lesson._id]?.data?.success ? (
                          <Check size={10} />
                        ) : (
                          <span>{lesson.order + 1}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Main Content */}
            <div
              className={`flex-1 overflow-hidden md:min-h-[500px] ${
                sidebarOpen ? "md:ml-0" : "ml-0"
              }`}
            >
              {selectedLesson ? (
                <div className="p-4 md:p-6 h-full flex flex-col">
                  {/* Lesson Title */}
                  <div className="text-center md:text-left flex-shrink-0 mb-6">
                    <h1 className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-cyan-400">
                      {selectedLesson.name}
                    </h1>
                    <div className="h-px bg-gradient-to-r from-lime-500/30 via-cyan-500/30 to-purple-500/30 mt-2"></div>
                  </div>

                  {/* Scrollable Content Area */}
                  <div className="flex-1 overflow-y-auto custom-scrollbar space-y-6 min-h-0">

                  {contentHeight === "long" ? (
                    // Long content: Resizable split layout with content left, code/results right
                    <div className="h-[calc(70vh)]">
                      <Split
                        key={selectedLesson._id}
                        sizes={horizontalSplitSizes}
                        onDragEnd={(sizes) => setHorizontalSplitSizes(sizes)}
                        minSize={[300, 250]}
                        maxSize={[800, Infinity]}
                        gutterSize={8}
                        className="split-horizontal"
                        direction="horizontal"
                      >
                        {/* Content Panel */}
                         <div className="flex flex-col h-full">
                            <div className="h-full">
                              <div className="p-6 bg-zinc-800/40 rounded-lg border border-zinc-700/50 shadow-lg h-full overflow-y-auto lesson-content custom-scrollbar">
                                <ReactMarkdown
                                  className="prose prose-invert max-w-none text-zinc-300 leading-relaxed text-sm"
                                  remarkPlugins={[remarkGfm]}
                                  rehypePlugins={[rehypeRaw]}
                                >
                                  {selectedLesson.lesson ?? ""}
                                </ReactMarkdown>
                              </div>
                            </div>
                          </div>

                        {/* Code and Results Panel */}
                        <div className="flex flex-col h-full">
                          <Split
                            sizes={[60, 40]}
                            minSize={[200, 150]}
                            gutterSize={8}
                            className="split-vertical"
                            direction="vertical"
                          >
                            {/* Code Editor */}
                            <div className="bg-zinc-800/40 rounded-lg overflow-hidden ring-1 ring-zinc-700/50 shadow-lg h-full flex flex-col">
                              <div className="flex items-center justify-between p-3 bg-zinc-800/70 border-b border-zinc-700/50 flex-shrink-0">
                                <div className="flex items-center">
                                  <span className="w-1 h-4 bg-lime-500 rounded-sm mr-2"></span>
                                  <span className="text-sm font-medium text-zinc-300">
                                    Editor
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 min-h-0">
                                <ContinuousCodeEditor
                                  ref={editorRef}
                                  lesson={selectedLesson}
                                />
                              </div>
                            </div>

                            {/* Test Results */}
                            <div className="bg-zinc-800/40 rounded-lg overflow-hidden ring-1 ring-zinc-700/50 shadow-lg h-full flex flex-col">
                              <div className="flex items-center justify-between p-3 bg-zinc-800/70 border-b border-zinc-700/50 flex-shrink-0">
                                <div className="flex items-center">
                                  <span className="w-1 h-4 bg-lime-500 rounded-sm mr-2"></span>
                                  <span className="text-sm font-medium text-zinc-300">
                                    Test Results
                                  </span>
                                </div>
                                {executionStatusMap[selectedLesson._id]?.data
                                  ?.success ? (
                                  <div className="flex items-center text-lime-400">
                                    <Check size={16} className="mr-1" />
                                    <span className="text-sm">Passed</span>
                                  </div>
                                ) : executionStatusMap[selectedLesson._id] ? (
                                  <div className="flex items-center text-red-400">
                                    <AlertCircle size={16} className="mr-1" />
                                    <span className="text-sm">Failed</span>
                                  </div>
                                ) : null}
                              </div>
                              <div className="p-4 flex-1 overflow-y-auto custom-scrollbar">
                                <TabbedResultPanel
                                  executionStatus={
                                    executionStatusMap[selectedLesson._id]
                                  }
                                  debugLogs={debugLogsMap[selectedLesson._id]}
                                />
                              </div>
                            </div>
                          </Split>
                        </div>
                      </Split>
                    </div>
                  ) : (
                    // Short content: Original layout with scrollable content
                    <>
                      <div className="prose prose-invert max-w-none">
                        <div className="p-4 md:p-6 bg-zinc-800/40 rounded-lg border border-zinc-700/50 shadow-lg max-h-64 overflow-y-auto lesson-content custom-scrollbar">
                          <ReactMarkdown
                            className="text-zinc-300 leading-relaxed text-sm"
                            children={selectedLesson.lesson}
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw]}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                        {/* Code Editor */}
                        <div className="bg-zinc-800/40 rounded-lg overflow-hidden ring-1 ring-zinc-700/50 shadow-lg">
                          <div className="flex items-center justify-between p-3 bg-zinc-800/70 border-b border-zinc-700/50">
                            <div className="flex items-center">
                              <span className="w-1 h-4 bg-lime-500 rounded-sm mr-2"></span>
                              <span className="text-sm font-medium text-zinc-300">
                                Editor
                              </span>
                            </div>
                          </div>
                          <div className="h-96 md:h-[450px]">
                            <ContinuousCodeEditor
                              ref={editorRef}
                              lesson={selectedLesson}
                            />
                          </div>
                        </div>

                        {/* Test Results */}
                        <div className="bg-zinc-800/40 rounded-lg overflow-hidden ring-1 ring-zinc-700/50 shadow-lg">
                          <div className="flex items-center justify-between p-3 bg-zinc-800/70 border-b border-zinc-700/50">
                            <div className="flex items-center">
                              <span className="w-1 h-4 bg-lime-500 rounded-sm mr-2"></span>
                              <span className="text-sm font-medium text-zinc-300">
                                Test Results
                              </span>
                            </div>
                            {executionStatusMap[selectedLesson._id]?.data
                              ?.success ? (
                              <div className="flex items-center text-lime-400">
                                <Check size={16} className="mr-1" />
                                <span className="text-sm">Passed</span>
                              </div>
                            ) : executionStatusMap[selectedLesson._id] ? (
                              <div className="flex items-center text-red-400">
                                <AlertCircle size={16} className="mr-1" />
                                <span className="text-sm">Failed</span>
                              </div>
                            ) : null}
                          </div>
                          <div className="p-4 h-[calc(450px-48px)] overflow-y-auto custom-scrollbar">
                            <TabbedResultPanel
                              executionStatus={
                                executionStatusMap[selectedLesson._id]
                              }
                              debugLogs={debugLogsMap[selectedLesson._id]}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  </div>

                  {/* Action Buttons - Fixed at bottom */}
                  <div className="flex justify-end gap-3 pt-4 flex-shrink-0 bg-zinc-900/80 backdrop-blur-sm -mx-4 md:-mx-6 px-4 md:px-6 pb-2">
                    {!user?.toString() ? (
                      <Link
                        target="_blank"
                        to={"/auth"}
                        className="flex items-center px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-md shadow-lg transition-colors"
                      >
                        <AlertCircle size={16} className="mr-2" />
                        Login To Run Tests
                      </Link>
                    ) : (
                      <>
                        {/* Show Solution button - only if solution exists and attempts > 3 */}
                        {selectedLesson?.solution &&
                          attemptCountMap[selectedLesson._id] > 3 && (
                            <button
                              onClick={() => setShowSolutionModal(true)}
                              className="flex items-center px-4 py-3 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white rounded-md shadow-lg transition-colors"
                            >
                              <Eye size={16} className="mr-2" />
                              Show Solution
                            </button>
                          )}
                        <button
                          onClick={() => handleRunCode(selectedLesson)}
                          disabled={!selectedLesson}
                          className={`flex items-center px-6 py-3 bg-gradient-to-r from-lime-600 to-cyan-700 hover:from-lime-700 hover:to-cyan-800 text-white rounded-md shadow-lg transition-colors ${
                            !selectedLesson ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                        >
                          <Play size={16} className="mr-2" />
                          Run Tests
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full p-8 md:p-12">
                  <div className="text-center">
                    <h2 className="text-xl text-zinc-400">
                      Select a problem to begin
                    </h2>
                    <p className="text-zinc-500 mt-2">
                      Choose from the list on the left side
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Solution Diff Modal */}
      <SolutionDiffModal
        isOpen={showSolutionModal}
        onClose={() => setShowSolutionModal(false)}
        userCode={selectedLesson?.progress?.[0]?.lastSubmission || ""}
        solutionCode={selectedLesson?.solution || ""}
      />
    </div>
  );
};

export default ContinuousPractice;
